import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ia as api,
  type Provedor,
  type ProvedorDisponivel,
  type ResultadoDoTeste,
} from '../api/ia'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import './times.css'

/**
 * As chaves dos provedores de IA.
 *
 * 🔴 **A chave nunca aparece aqui.** A tela mostra que está configurada, de
 * onde vem e os quatro últimos caracteres — o suficiente para conferir o que se
 * acabou de colar. Segredo que a API devolve é segredo que vaza no log do
 * navegador, no HAR e no print de tela.
 *
 * E **campo vazio não apaga**: o back preserva a chave existente. É o erro
 * clássico de formulário de senha, e aqui ele derrubaria o atendimento.
 */
export function ConfiguracaoIa() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [lista, setLista] = useState<ProvedorDisponivel[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [rascunho, setRascunho] = useState<Record<string, string>>({})
  const [salvando, setSalvando] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  const [testando, setTestando] = useState<string | null>(null)
  const [teste, setTeste] = useState<Record<string, ResultadoDoTeste>>({})

  const gerencia = pode('ParametroCtgf')

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await api.provedores())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function salvar(provedor: Provedor) {
    const chave = (rascunho[provedor] ?? '').trim()
    if (!chave) return

    setSalvando(provedor)
    try {
      await api.gravarChave(provedor, chave)
      setRascunho({ ...rascunho, [provedor]: '' })
      setRecado(t('chaves.salva', { provedor }))
      await carregar()
    } finally {
      setSalvando(null)
    }
  }

  async function testar(provedor: Provedor) {
    setTestando(provedor)
    try {
      setTeste({ ...teste, [provedor]: await api.testarChave(provedor) })
    } finally {
      setTestando(null)
    }
  }

  async function apagar(provedor: Provedor) {
    await api.apagarChave(provedor)
    setRecado(t('chaves.apagada', { provedor }))
    await carregar()
  }

  return (
    <>
      <header className="times__topo">
        <div>
          <h1 className="times__titulo">{t('chaves.titulo')}</h1>
          <p className="times__descricao">{t('chaves.descricao')}</p>
        </div>
      </header>

      <Aviso tom="info">{t('chaves.nuncaVolta')}</Aviso>

      {recado && <Aviso tom="sucesso">{recado}</Aviso>}

      {falhou ? (
        <Estado
          tipo="erro"
          titulo={t('chaves.erroAoCarregar')}
          acao={<Botao onClick={() => void carregar()}>{t('chaves.tentarDeNovo')}</Botao>}
        />
      ) : lista === null ? (
        <Estado tipo="carregando" titulo={t('chaves.carregando')} />
      ) : (
        <ul className="times__lista">
          {lista.map((p) => (
            <li key={p.provedor}>
              <Cartao>
                <div className="times__cabecalho">
                  <h2 className="times__nome">
                    {p.provedor}
                    <span className="times__ausente">
                      {p.configurado
                        ? t('chaves.configurada', { fim: p.ultimosQuatro ?? '', origem: t(`chaves.origem.${p.origem}`) })
                        : t('chaves.naoConfigurada')}
                    </span>
                  </h2>
                </div>

                {/* O recuo pelo arquivo é dito em voz alta: quem opera precisa
                    saber que aquela chave não está no cofre, e que trocá-la
                    ainda exige acesso à máquina. */}
                {p.origem === 'arquivo' && <Aviso tom="alerta">{t('chaves.vemDoArquivo')}</Aviso>}

                {teste[p.provedor] && (
                  <Aviso tom={teste[p.provedor].ok ? 'sucesso' : 'alerta'}>
                    {teste[p.provedor].mensagem}
                  </Aviso>
                )}

                {gerencia && (
                  <div className="times__edicao">
                    <Campo
                      type="password"
                      autoComplete="off"
                      rotulo={t('chaves.nova', { provedor: p.provedor })}
                      value={rascunho[p.provedor] ?? ''}
                      onChange={(e) => setRascunho({ ...rascunho, [p.provedor]: e.target.value })}
                      ajuda={t('chaves.vazioMantem')}
                    />

                    <div className="times__acoes">
                      <Botao
                        variante="primario"
                        carregando={salvando === p.provedor}
                        onClick={() => void salvar(p.provedor)}
                      >
                        {t('chaves.salvar')}
                      </Botao>

                      {/* 🔴 Testa a chave que está valendo, não o rascunho: a
                          chave da IA é a única cuja falha derruba o atendimento
                          inteiro, e era a única sem conferência no cadastro. */}
                      <Botao
                        variante="discreto"
                        carregando={testando === p.provedor}
                        onClick={() => void testar(p.provedor)}
                      >
                        {t('chaves.testar')}
                      </Botao>

                      {p.origem === 'portal' && (
                        <Botao variante="discreto" onClick={() => void apagar(p.provedor)}>
                          {t('chaves.apagar')}
                        </Botao>
                      )}
                    </div>
                  </div>
                )}
              </Cartao>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
